import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useSession, useViewport } from '@/hooks';
import { Avatar, Button, Popover } from '@/ui';
import { Icon } from '@/icons';
import { LogoTriad } from '@/icons/brand/LogoTriad';
import { useSettingsSidebar } from './useSettingsSidebar';
import { focusRing } from './update-logs/styles';

const Skip = styled.a`
  ${focusRing}
  position: absolute;
  left: 16px;
  top: -80px;
  padding: 12px;
  z-index: 30;
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  &:focus {
    top: 12px;
  }
`;
const Topbar = styled.header`
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 20px;
  min-height: 78px;
  padding: 12px 28px;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surfaceChrome};
  @media (max-width: 767.98px) {
    padding: 12px 16px;
    gap: 14px;
    flex-wrap: wrap;
    position: relative;
  }
`;
const Brand = styled(Link)`
  ${focusRing} display: flex;
  align-items: center;
  gap: 12px;
  color: inherit;
  text-decoration: none;
  flex-shrink: 0;
  & > span {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  & strong {
    font-size: 1.2rem;
    font-weight: 600;
    letter-spacing: -0.025em;
  }
  & small {
    color: ${({ theme }) => theme.color.textMuted};
    font-size: 0.6rem;
  }
  @media (max-width: 1000px) {
    & small {
      display: none;
    }
  }
`;
const Search = styled.form`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  width: min(100%, 620px);
  margin: 0 auto;
  padding: 10px 14px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.color.textMuted};
  background: ${({ theme }) => theme.color.surface};
  &:focus-within {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
  }
  & input {
    flex: 1;
    min-width: 0;
    padding: 0;
    background: none;
    border: 0;
    outline: 0;
    color: ${({ theme }) => theme.color.textPrimary};
    font: inherit;
    font-size: ${({ theme }) => theme.fontSize.xs};
  }
  @media (max-width: 767.98px) {
    order: 3;
    width: 100%;
  }
`;
const Account = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  @media (max-width: 767.98px) {
    margin-left: auto;
    gap: 10px;
  }
`;
const AccountTrigger = styled.button`
  ${focusRing} display: flex;
  align-items: center;
  gap: 10px;
  color: inherit;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  padding: 2px;
  font: inherit;
  cursor: pointer;
  text-align: left;
  & > span[data-account-summary] {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }
  & strong {
    font-size: ${({ theme }) => theme.fontSize.xs};
    max-width: 130px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & small {
    color: ${({ theme }) => theme.color.textMuted};
    font-size: 0.6rem;
    white-space: nowrap;
  }
  @media (max-width: 900px) {
    & > span[data-account-summary] {
      display: none;
    }
  }
`;
const MenuButton = styled.button`
  ${focusRing} display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 10px;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.color.textPrimary};
  cursor: pointer;
  font: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm};
  &:hover {
    background: ${({ theme }) => theme.color.hover};
  }
`;
const NavItem = styled(Link)`
  ${focusRing} flex-shrink: 0;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  color: ${({ theme }) => theme.color.textMuted};
  text-decoration: none;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.sm};
  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
    background: ${({ theme }) => theme.color.hover};
  }
  &[aria-current='page'] {
    color: ${({ theme }) => theme.color.brandFrom};
    background: ${({ theme }) => theme.color.brandFrom}1f;
  }
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
const BackButton = styled(Button)`
  flex-shrink: 0;
  padding: 8px;
`;

/** Shared chrome stays present when navigating between Settings destinations. */
export function SettingsHeader() {
  const { currentUser, signOut } = useSession();
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const viewport = useViewport();
  const { collapsed, toggle } = useSettingsSidebar();
  function handleBack() {
    if (location.key === 'default') navigate('/');
    else navigate(-1);
  }
  function search(event: FormEvent) {
    event.preventDefault();
    navigate(`/explore${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ''}`);
  }
  return (
    <Topbar role="banner" aria-label="Settings header">
      <Skip href="#settings-main">Skip to settings content</Skip>
      <HeaderControls>
        <BackButton variant="ghost" onClick={handleBack} aria-label="Back">
          <Icon name="arrow-left" size={16} />
        </BackButton>
        {viewport !== 'mobile' && (
          <BackButton
            variant="ghost"
            onClick={toggle}
            aria-label={collapsed ? 'Expand settings sidebar' : 'Collapse settings sidebar'}
            title={collapsed ? 'Expand settings sidebar' : 'Collapse settings sidebar'}
            aria-expanded={!collapsed}
            aria-controls="settings-sidebar"
          >
            <Icon name="panel-left" size={20} />
          </BackButton>
        )}
      </HeaderControls>
      <Brand to="/" aria-label="TruXSocial home">
        <LogoTriad size={37} />
        <span>
          <strong>TruXSocial</strong>
          <small>Real People, Real Conversations</small>
        </span>
      </Brand>
      <Search role="search" aria-label="Global search" onSubmit={search}>
        <Icon name="search" size={18} />
        <input
          type="search"
          aria-label="Search TruXSocial"
          placeholder="Search TruXSocial…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </Search>
      <Account>
        <NavItem to="/notifications" aria-label="View notifications" title="Notifications">
          <Icon name="bell" size={22} />
        </NavItem>
        <Popover
          trigger={
            <AccountTrigger aria-label="Account menu">
              <Avatar
                name={currentUser?.name ?? 'Account'}
                avatarKey={currentUser?.avatarKey}
                size={34}
              />
              <span data-account-summary>
                <strong>{currentUser?.name ?? 'Account'}</strong>
                <small>
                  {currentUser?.joined ? `Member since ${currentUser.joined}` : currentUser?.handle}
                </small>
              </span>
              <Icon name="chevron-down" size={16} />
            </AccountTrigger>
          }
        >
          <MenuButton role="menuitem" onClick={() => navigate('/profile')}>
            <Icon name="user" size={16} />
            Profile
          </MenuButton>
          <MenuButton role="menuitem" onClick={() => navigate('/settings')}>
            <Icon name="settings" size={16} />
            Settings
          </MenuButton>
          <MenuButton
            role="menuitem"
            onClick={() => {
              signOut();
              navigate('/');
            }}
          >
            <Icon name="logout" size={16} />
            Sign out
          </MenuButton>
        </Popover>
      </Account>
    </Topbar>
  );
}
